import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Debounce a value - returns the debounced value after the specified delay
 */
export function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}

/**
 * Debounce a function - returns a debounced version of the function
 */
export function useDebounceFn<F extends (...args: any[]) => any>(
  fn: F,
  delay: number,
  _external_dependencies?: any[],
) {
  const fnRef = useRef<F>(fn);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  fnRef.current = fn;

  const debouncedFn = useCallback(
    (...args: Parameters<F>) => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }

      debounceRef.current = setTimeout(() => fnRef.current(...args), delay);
    },
    [delay],
  );

  return debouncedFn;
}
